import {COLORS, RADIUS, SkeletonBlock, SPACING} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {FC} from 'react';

const PILL_WIDTHS = ['64px', '96px', '52px', '80px', '72px'] as const;
const CARD_COUNT = 3;
const CARD_SIZE = '226px';
const HEADER_ICON_SIZE = '16px';
const PILL_HEIGHT = '32px';

// Stands in for the trending section both while its chunk downloads and while
// its request runs, so the panel only ever shows one loading state. It mirrors
// the three sliders the section is built from: the trending pills, the
// sponsored teaser and the popular cards. It cannot import those sliders: a
// fallback renders before the chunk it replaces, so importing them would pull
// that chunk into the parent bundle. The same goes for the Pill atom: this
// fallback sits in the eager bundle of every page, so the pill geometry is
// restated here instead of imported.
// data-panel-placeholder is what the search flyout reads to hold its full
// height until the whole panel has arrived.
export const SearchSuggestionsTrendingSkeleton: FC = () => (
  <div
    data-testid="searchSuggestionsTrendingLoading"
    data-panel-placeholder
    aria-hidden="true"
  >
    <Section>
      <Header>
        <SkeletonBlock
          $width={HEADER_ICON_SIZE}
          $height={HEADER_ICON_SIZE}
          disableSpaceBelow
        />
        <SkeletonBlock $width="124px" $height="12px" disableSpaceBelow />
      </Header>
      <Row>
        {PILL_WIDTHS.map((width, index) => (
          <PillPlaceholder key={`trending-pill-skeleton-${index}`}>
            <SkeletonBlock $width={width} $height="12px" disableSpaceBelow />
          </PillPlaceholder>
        ))}
      </Row>
    </Section>
    <Section>
      <SponsoredRow>
        <SponsoredTile disableSpaceBelow />
      </SponsoredRow>
    </Section>
    <Section>
      <Header>
        <SkeletonBlock
          $width={HEADER_ICON_SIZE}
          $height={HEADER_ICON_SIZE}
          disableSpaceBelow
        />
        <SkeletonBlock $width="96px" $height="12px" disableSpaceBelow />
      </Header>
      <Row>
        {Array.from({length: CARD_COUNT}, (_, index) => (
          <Card
            key={`trending-card-skeleton-${index}`}
            $width={CARD_SIZE}
            $height={CARD_SIZE}
            disableSpaceBelow
          />
        ))}
      </Row>
    </Section>
  </div>
);

const Section = styled.div`
  padding: ${SPACING['X.Lg']} 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING['X.Md']};
  margin: 0 ${SPACING['X.Lg']} ${SPACING['X.Lg']};
`;

const Row = styled.div`
  display: flex;
  gap: ${SPACING['X.Md']};
  padding: 0 ${SPACING['X.Lg']};
  overflow: hidden;
`;

const PillPlaceholder = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: ${PILL_HEIGHT};
  padding: 0 12px 0 10px;
  border-radius: ${RADIUS.Round};
  background-color: ${COLORS['Fill.Grey.Primary']};
`;

const SponsoredRow = styled.div`
  padding: 0 ${SPACING['X.Lg']};
`;

const SponsoredTile = styled(SkeletonBlock)`
  && {
    height: auto;
    aspect-ratio: 1;
  }
`;

const Card = styled(SkeletonBlock)`
  flex-shrink: 0;
  max-width: 100%;
  border-radius: ${RADIUS.Md};
`;
